import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DataProvider from "../../apis";
import { Button, notification, Input } from "antd";
import med from "../../assets/images/logohd.png";
import bg from "../../assets/images/bg2.png";
import doc from "../../assets/images/doc4.avif";
import EmailInput from "../../components/form/email-input";
import PasswordInput from "../../components/form/password-input";
import { NotificationType } from "../../utils";
import { useAppStore } from "../../store";

type MigrationStep = "otp" | "password" | null;

function getSignInErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const ax = (error as { response?: { data?: { message?: string }; status?: number } }).response;
    if (ax?.data?.message) return ax.data.message;
    if (ax?.status === 400) return "Invalid credentials.";
  }
  if (error && typeof error === "object" && "message" in error && typeof (error as Error).message === "string") {
    return (error as Error).message;
  }
  return "An error occurred during sign-in. Please try again.";
}

export default function Login() {
  const [api, contextHolder] = notification.useNotification();
  const { setDoctor, setToken, setUser } = useAppStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [migrationStep, setMigrationStep] = useState<MigrationStep>(null);
  const [migrationEmail, setMigrationEmail] = useState<string>("");
  const [migrationPhone, setMigrationPhone] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [migrationLoading, setMigrationLoading] = useState(false);

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string
  ) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  async function onSubmit() {
    if (email.length < 5) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Email must not be empty. Type your email address."
      );
    }

    if (password.length < 8) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Password must be at least 8 characters."
      );
    }

    setLoading(true);
    try {
      const { data } = await DataProvider.signIn({ email, password });

      if ((data as unknown as { requiresMigration?: boolean }).requiresMigration) {
        const migration = data as unknown as { requiresMigration: true; message: string; email: string; phoneNumber: string };
        setMigrationEmail(migration.email);
        setMigrationPhone(migration.phoneNumber ?? "");
        setMigrationStep("otp");
        setLoading(false);
        openNotificationWithIcon("info", "Password migration", migration.message ?? "OTP sent for password migration.");
        return;
      }

      const authData = data as unknown as { user: unknown; token: string; refreshToken: string };
      setToken({
        token: authData.token,
        refreshToken: authData.refreshToken,
      });
      setUser(authData.user);

      const { data: doctorData } = await DataProvider.getDoctorProfile();
      const failedPayload = doctorData as { status?: string; message?: string; code?: string } | undefined;
      if (failedPayload?.status === "failed") {
        setLoading(false);
        if (failedPayload?.code === "DOCTOR_PROFILE_NOT_SET_UP") {
          openNotificationWithIcon(
            "info",
            "Complete registration",
            failedPayload.message ?? "Complete your doctor profile to continue."
          );
          navigate("/set-up");
          return;
        }
        openNotificationWithIcon(
          "error",
          "Failed",
          failedPayload?.message ?? "Account not registered as a doctor. Sign up!"
        );
        return;
      }

      if (doctorData) {
        setDoctor(doctorData);
      }
      setLoading(false);
      navigate("/");
    } catch (e) {
      setLoading(false);
      openNotificationWithIcon("error", "Failed", getSignInErrorMessage(e));
    }
  }

  function exitMigration() {
    setMigrationStep(null);
    setMigrationEmail("");
    setMigrationPhone("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
  }

  async function onVerifyOtp() {
    if (!otp.trim()) {
      return openNotificationWithIcon("warning", "Warning", "Enter the 6-digit OTP sent to your email.");
    }
    setMigrationLoading(true);
    try {
      await DataProvider.verifyOtpMigration({
        email: migrationEmail || undefined,
        phoneNumber: migrationPhone || undefined,
        otpCode: otp.trim(),
      });
      setMigrationStep("password");
      setOtp("");
      openNotificationWithIcon("success", "Verified", "Now set your new password.");
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "response" in e && typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === "string"
        ? (e as { response: { data: { message: string } } }).response.data.message
        : "Invalid or expired OTP. Please try again.";
      openNotificationWithIcon("error", "Verification failed", msg);
    } finally {
      setMigrationLoading(false);
    }
  }

  async function onSubmitNewPassword() {
    if (newPassword.length < 8) {
      return openNotificationWithIcon("warning", "Warning", "Password must be at least 8 characters.");
    }
    if (newPassword !== confirmPassword) {
      return openNotificationWithIcon("warning", "Warning", "Passwords do not match.");
    }
    setMigrationLoading(true);
    try {
      await DataProvider.setNewPassword({
        email: migrationEmail || undefined,
        phoneNumber: migrationPhone || undefined,
        newPassword,
      });
      openNotificationWithIcon("success", "Password set", "You can now sign in with your new password.");
      exitMigration();
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "response" in e && typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message === "string"
        ? (e as { response: { data: { message: string } } }).response.data.message
        : "Failed to set password. Please try again.";
      openNotificationWithIcon("error", "Failed", msg);
    } finally {
      setMigrationLoading(false);
    }
  }

  const renderMigrationContent = (isMobile: boolean) => {
    if (!migrationStep) return null;
    const wrapperClass = isMobile ? "w-[90%]" : "w-[80%]";
    return (
      <div className={wrapperClass}>
        <div className="mb-4">
          <h1 className="text-2xl font-bold mb-1">
            {migrationStep === "otp" ? "Verify OTP" : "Set new password"}
          </h1>
          <p className="text-base text-gray-500">
            {migrationStep === "otp"
              ? "Enter the OTP sent to your phone or email."
              : "Choose a new password (min 8 characters)."}
          </p>
          {(migrationEmail || migrationPhone) && (
            <p className="text-sm text-gray-400 mt-1" role="status">
              {migrationEmail && `Account: ${migrationEmail}`}
              {migrationEmail && migrationPhone && " • "}
              {migrationPhone && `Phone: ${migrationPhone}`}
            </p>
          )}
        </div>
        {migrationStep === "otp" ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">OTP</label>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
                maxLength={8}
                aria-label="One-time password"
                className="w-full"
              />
            </div>
            <Button
              type="primary"
              size="large"
              onClick={onVerifyOtp}
              loading={migrationLoading}
              className="bg-[#1A94EC] w-full"
              aria-label="Verify OTP"
            >
              Verify OTP
            </Button>
          </>
        ) : (
          <>
            <div className="mb-3">
              <PasswordInput
                required
                password={newPassword}
                setPassword={setNewPassword}
                placeholder="New password (min 8 characters)"
              />
            </div>
            <div className="mb-4">
              <PasswordInput
                required
                password={confirmPassword}
                setPassword={setConfirmPassword}
                placeholder="Confirm new password"
              />
            </div>
            <Button
              type="primary"
              size="large"
              onClick={onSubmitNewPassword}
              loading={migrationLoading}
              className="bg-[#1A94EC] w-full"
              aria-label="Set new password"
            >
              Set password and continue
            </Button>
          </>
        )}
        <Button type="link" onClick={exitMigration} className="mt-4 w-full" aria-label="Back to sign in">
          Back to sign in
        </Button>
      </div>
    );
  };

  const renderLoginForm = (isMobile: boolean) => (
    <>
      <div className={isMobile ? "w-[90%] my-4" : "w-[80%] my-4"}>
        <h1 className="text-2xl font-bold mb-1">Login</h1>
        <p className="text-base text-gray-500">Sign in to your account.</p>
      </div>
      <div className={isMobile ? "w-[90%]" : "w-[80%]"}>
        <EmailInput
          email={email}
          setEmail={setEmail}
          placeholder="Enter your email address."
        />
        <PasswordInput
          required
          forgot
          password={password}
          setPassword={setPassword}
          placeholder="*****************"
        />
        <Button
          type="primary"
          onClick={onSubmit}
          size="large"
          loading={loading}
          className="bg-[#1A94EC] w-full"
          aria-label="Sign in"
        >
          {loading ? "Loading..." : "Sign in"}
        </Button>

        <div className="w-full flex justify-center items-center mt-12">
          <p className="text-[#ADADAD] text-base">
            Don’t have an account?{" "}
            <Link to="/sign-up" className="text-[#1A94EC]">
              Create an account
            </Link>
          </p>
        </div>
        <div className={isMobile ? "absolute bottom-7 right-5 flex justify-center items-center" : "absolute bottom-5 right-9 flex"}>
          <img src={med} className="h-8 mx-4" alt="MedPharma" />
        </div>
      </div>
    </>
  );

  return (
    <div className="w-full h-full">
      <div
        className="h-screen lg:hidden flex justify-center items-center w-full bg-cover bg-[#0081D4] bg-blend-overlay"
        style={{ backgroundImage: `url(${bg})` }}
      >
        <div className="min-h-[60%] sm:w-[60%] w-[90%] rounded bg-white flex flex-col justify-center items-center pb-4">
          {migrationStep ? renderMigrationContent(true) : renderLoginForm(true)}
        </div>
      </div>

      <div className="h-screen w-full lg:flex hidden justify-between items-center">
        <div
          className="h-full flex flex-col justify-center items-center w-[50%] bg-cover bg-[#0081D4] bg-blend-overlay"
          style={{ backgroundImage: `url(${bg})` }}
        >
          <div className="absolute top-6 left-4">
            <img src={med} className="h-8 w-28" alt="MedPharma" />
          </div>
          <div className="flex flex-col justify-center items-center text-center md:w-[70%]">
            <h1 className="text-2xl leading-10 text-white">
              Medpharma's Consultation
            </h1>
            <h1 className="text-2xl leading-10 text-white">
              I have trippled my income as a Specialist.
            </h1>
          </div>
          <div className="my-4 flex flex-col justify-center w-full items-center">
            <div className="h-20 w-20 rounded-full border-4 border-white">
              <img src={doc} alt="" className="h-full w-full rounded-full" />
            </div>
            <h3 className="text-white mt-2 font-medium">Dr. Edwin Mensah</h3>
            <p className="text-[#7ED0FD]">Tamale Teaching Hospital</p>
          </div>
        </div>
        <div className="h-full w-[47%] flex flex-col justify-center items-center">
          {migrationStep ? renderMigrationContent(false) : renderLoginForm(false)}
        </div>
      </div>
      {contextHolder}
    </div>
  );
}
